import { PlaybackState } from "./PlaybackState";
import { MediaType, detectMediaType } from "./MediaTypes";
import { PlayerEvent } from "./PlayerEvents";
import { createPlaybackEventBus } from "./PlaybackEventBus";
import { PlaybackPlatformEvent } from "./PlaybackPlatformEvents";
import {
  createLifecycleManager,
  LifecyclePhase,
} from "./PlaybackLifecycle";
import {
  normalizePlaybackError,
  createPlaybackError,
  PlaybackErrorCode,
} from "./PlaybackErrors";
import { ErrorRecoveryService } from "../Services/ErrorRecoveryService";
import { createStreamingProviderRegistry } from "../Streaming/StreamingProviderRegistry";
import { StreamingProviderEvents } from "../Streaming/StreamingProvider";
import { createQualityManager } from "../Services/QualityManager";
import { createBufferMonitor } from "../Services/BufferMonitor";
import {
  SurfaceKind,
  PlaybackProviderEvents,
} from "../Providers/Playback/PlaybackProviderContract";
import { createCapabilityRegistry } from "../Providers/Playback/CapabilityRegistry";
import { tpgTrace, tpgSurfaceAudit } from "../production/tpgPipelineTrace";

/**
 * Framework-agnostic playback engine.
 * Streaming / embed SDKs are isolated behind PlaybackProviders.
 * No Pentecost+ business logic. No casting SDK imports.
 * Sprint 6A: Event Bus, capabilities, lifecycle, typed errors.
 */
export function createPlaybackEngine(options) {
  options = options || {};
  var emitter = createPlaybackEventBus();
  var video = null;
  var embedRoot = null;
  var activeProvider = null;
  var providerUnsubs = [];
  var registry = createStreamingProviderRegistry(options.streaming || {});
  var quality = createQualityManager();
  var bufferMonitor = createBufferMonitor(options.bufferMonitor || {});
  var capabilities = createCapabilityRegistry();
  // Unbound engine defaults to video surface; avoid advertising PiP deny before
  // the first provider bind (intro and post-handoff remount both hit this path).
  capabilities.clear();
  var lifecycle = createLifecycleManager({ bus: emitter });
  var recovery = new ErrorRecoveryService({
    maxRetries: options.maxRetries != null ? options.maxRetries : 3,
    retryDelayMs: options.retryDelayMs != null ? options.retryDelayMs : 1200,
  });

  var state = {
    playbackState: PlaybackState.IDLE,
    currentTime: 0,
    duration: 0,
    bufferedEnd: 0,
    volume: options.volume != null ? options.volume : 1,
    muted: !!options.muted,
    playbackRate: options.playbackRate != null ? options.playbackRate : 1,
    mediaType: MediaType.UNKNOWN,
    source: null,
    error: null,
    fullscreen: false,
    pip: false,
    providerName: null,
    surfaceKind: SurfaceKind.VIDEO,
    capabilities: capabilities.getCapabilities(),
    lifecyclePhase: lifecycle.getPhase(),
  };

  function setPlaybackState(next) {
    if (state.playbackState === next) {
      return;
    }
    var prev = state.playbackState;
    state.playbackState = next;
    state.lifecyclePhase = lifecycle.syncFromPlaybackState(next);
    emitter.emit("statechange", { state: next, previous: prev });
    emitter.emit(PlayerEvent.STATE_CHANGED, {
      state: next,
      previous: prev,
      lifecyclePhase: state.lifecyclePhase,
    });
    emitter.emit(next, state);
  }

  function snapshot() {
    return Object.assign({}, state, {
      quality: quality.getSnapshot(),
      buffer: bufferMonitor.getSnapshot(),
    });
  }

  function clearProvider() {
    providerUnsubs.forEach(function (u) {
      if (typeof u === "function") u();
    });
    providerUnsubs = [];
    if (activeProvider) {
      try {
        activeProvider.destroy();
      } catch (e) {
        /* ignore */
      }
      activeProvider = null;
    }
    state.providerName = null;
    capabilities.clear();
    state.capabilities = capabilities.getCapabilities();
  }

  function bindProvider(provider) {
    clearProvider();
    activeProvider = provider;
    state.providerName = provider.getName();
    state.surfaceKind =
      typeof provider.getSurfaceKind === "function"
        ? provider.getSurfaceKind()
        : SurfaceKind.VIDEO;
    state.capabilities = capabilities.updateFromProvider(provider);
    emitter.emit(PlaybackPlatformEvent.CAPABILITIES_CHANGED, state.capabilities);
    tpgTrace("CAPABILITIES_CHANGED", {
      provider: state.providerName,
      surfaceKind: state.surfaceKind,
      supportsPiP: !!(state.capabilities && state.capabilities.supportsPiP),
      supportsFullscreen: !!(
        state.capabilities && state.capabilities.supportsFullscreen
      ),
    });
    tpgTrace("ACTIVE_PROVIDER", {
      provider: state.providerName,
      surfaceKind: state.surfaceKind,
    });
    tpgTrace(
      "SURFACE_AUDIT",
      tpgSurfaceAudit({
        providerName: state.providerName,
        surfaceKind: state.surfaceKind,
        video: video,
        embed: embedRoot,
        pipTarget:
          state.surfaceKind === SurfaceKind.EMBED
            ? "none-embed"
            : "HTMLVideoElement",
        pipMode:
          state.surfaceKind === SurfaceKind.EMBED ? "unsupported" : "element",
        fullscreenTarget: "shell",
        muteTarget:
          state.surfaceKind === SurfaceKind.EMBED
            ? "provider:" + state.providerName
            : "HTMLVideoElement",
        volumeTarget:
          state.surfaceKind === SurfaceKind.EMBED
            ? "provider:" + state.providerName
            : "HTMLVideoElement",
      })
    );

    if (state.surfaceKind === SurfaceKind.EMBED) {
      if (video) {
        video.style.display = "none";
        video.removeAttribute("src");
        try {
          video.load();
        } catch (e) {
          /* ignore */
        }
      }
      if (embedRoot) {
        embedRoot.style.display = "block";
        provider.attach(embedRoot);
      }
    } else {
      if (embedRoot) {
        embedRoot.style.display = "none";
        embedRoot.innerHTML = "";
      }
      if (video) {
        video.style.display = "";
        provider.attach(video);
      }
    }

    emitter.emit(PlayerEvent.PROVIDER_CHANGED, {
      provider: state.providerName,
      surfaceKind: state.surfaceKind,
    });

    if (typeof provider.on === "function") {
      providerUnsubs.push(
        provider.on(StreamingProviderEvents.MANIFEST_PARSED, function (data) {
          quality.setAvailableLevels(data.levels || []);
          quality.reportLevelSwitched(
            data.currentLevel != null ? data.currentLevel : -1
          );
          emitter.emit(PlayerEvent.QUALITY_CHANGED, quality.getSnapshot());
          var subTracks =
            activeProvider &&
            typeof activeProvider.getSubtitleTracks === "function"
              ? activeProvider.getSubtitleTracks()
              : [];
          emitter.emit(PlayerEvent.PROVIDER_SUBTITLES_CHANGED, {
            tracks: subTracks || [],
          });
        })
      );
      providerUnsubs.push(
        provider.on(StreamingProviderEvents.LEVEL_SWITCHED, function (data) {
          quality.reportLevelSwitched(data.level);
          emitter.emit(PlayerEvent.QUALITY_CHANGED, quality.getSnapshot());
        })
      );
      providerUnsubs.push(
        provider.on(StreamingProviderEvents.ERROR, function (err) {
          handleProviderError(err);
        })
      );
      providerUnsubs.push(
        provider.on(StreamingProviderEvents.RETRY, function (info) {
          emitter.emit(PlayerEvent.RETRY_ATTEMPT, info);
          bufferMonitor.markRecovery(info);
        })
      );
      providerUnsubs.push(
        provider.on(PlaybackProviderEvents.TIME, function (payload) {
          if (!payload) return;
          state.currentTime = payload.currentTime || 0;
          if (payload.duration) state.duration = payload.duration;
          emitter.emit("timeupdate", {
            currentTime: state.currentTime,
            duration: state.duration,
          });
          emitter.emit(PlayerEvent.TIME_UPDATE, {
            currentTime: state.currentTime,
            duration: state.duration,
          });
        })
      );
      providerUnsubs.push(
        provider.on(PlaybackProviderEvents.PLAY, function () {
          setPlaybackState(PlaybackState.PLAYING);
          emitter.emit(PlayerEvent.PLAYBACK_STARTED, snapshot());
        })
      );
      providerUnsubs.push(
        provider.on(PlaybackProviderEvents.PAUSE, function () {
          if (state.playbackState !== PlaybackState.ENDED) {
            setPlaybackState(PlaybackState.PAUSED);
            emitter.emit(PlayerEvent.PLAYBACK_PAUSED, snapshot());
          }
        })
      );
      providerUnsubs.push(
        provider.on(PlaybackProviderEvents.ENDED, function () {
          tpgTrace("STATE", {
            message: "provider_ENDED",
            provider: state.providerName,
          });
          setPlaybackState(PlaybackState.ENDED);
          emitter.emit("ended");
          emitter.emit(PlayerEvent.PLAYBACK_ENDED, snapshot());
        })
      );
      providerUnsubs.push(
        provider.on(PlaybackProviderEvents.ERROR, function (err) {
          handleProviderError(err);
        })
      );
    }
  }

  function handleProviderError(err) {
    var normalized = normalizePlaybackError(err, {
      providerName: state.providerName,
    });
    // Recoverable HLS/network errors are self-healed by the provider; keep
    // UI in buffering instead of a sticky error overlay during retries.
    if (normalized.recoverable || (err && err.recoverable)) {
      normalized.recoverable = true;
      state.error = normalized;
      setPlaybackState(PlaybackState.BUFFERING);
      emitter.emit(PlayerEvent.RETRY_ATTEMPT, normalized);
      return;
    }
    state.error = normalized;
    setPlaybackState(PlaybackState.ERROR);
    emitter.emit("error", normalized);
    emitter.emit(PlayerEvent.PLAYBACK_ERROR, normalized);
  }

  function bindVideo(el) {
    unbindVideo();
    video = el;
    if (!video) {
      return;
    }

    video.volume = state.volume;
    video.muted = state.muted;
    video.playbackRate = state.playbackRate;
    bufferMonitor.attach(video);
    bufferMonitor.start();

    video.addEventListener("loadstart", onLoadStart);
    video.addEventListener("loadedmetadata", onLoadedMetadata);
    video.addEventListener("canplay", onCanPlay);
    video.addEventListener("playing", onPlaying);
    video.addEventListener("pause", onPause);
    video.addEventListener("waiting", onWaiting);
    video.addEventListener("seeking", onSeeking);
    video.addEventListener("seeked", onSeeked);
    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("progress", onProgress);
    video.addEventListener("ended", onEnded);
    video.addEventListener("error", onError);
    video.addEventListener("ratechange", onRateChange);
    video.addEventListener("volumechange", onVolumeChange);
    video.addEventListener("enterpictureinpicture", onEnterPiP);
    video.addEventListener("leavepictureinpicture", onLeavePiP);

    bindFullscreenListeners();
  }

  /**
   * Attach both the HTMLVideoElement and an embed mount point.
   * Video providers use `video`; embed providers use `embed`.
   * @param {object} surface
   * @param {HTMLVideoElement} [surface.video]
   * @param {HTMLElement} [surface.embed]
   */
  function attachMediaSurface(surface) {
    surface = surface || {};
    embedRoot = surface.embed || null;
    if (surface.video) {
      bindVideo(surface.video);
    } else if (!embedRoot) {
      unbindVideo();
    }
  }

  function unbindVideo() {
    bufferMonitor.stop();
    unbindFullscreenListeners();
    if (!video) {
      return;
    }
    video.removeEventListener("loadstart", onLoadStart);
    video.removeEventListener("loadedmetadata", onLoadedMetadata);
    video.removeEventListener("canplay", onCanPlay);
    video.removeEventListener("playing", onPlaying);
    video.removeEventListener("pause", onPause);
    video.removeEventListener("waiting", onWaiting);
    video.removeEventListener("seeking", onSeeking);
    video.removeEventListener("seeked", onSeeked);
    video.removeEventListener("timeupdate", onTimeUpdate);
    video.removeEventListener("progress", onProgress);
    video.removeEventListener("ended", onEnded);
    video.removeEventListener("error", onError);
    video.removeEventListener("ratechange", onRateChange);
    video.removeEventListener("volumechange", onVolumeChange);
    video.removeEventListener("enterpictureinpicture", onEnterPiP);
    video.removeEventListener("leavepictureinpicture", onLeavePiP);
    video.removeEventListener("webkitbeginfullscreen", onWebkitBeginFullscreen);
    video.removeEventListener("webkitendfullscreen", onWebkitEndFullscreen);
    video = null;
  }

  function syncFullscreenState(isFs) {
    var was = state.fullscreen;
    state.fullscreen = !!isFs;
    emitter.emit("fullscreenchange", state.fullscreen);
    emitter.emit(PlayerEvent.FULLSCREEN_CHANGED, state.fullscreen);
    if (was !== state.fullscreen) {
      emitter.emit(
        state.fullscreen
          ? PlayerEvent.FULLSCREEN_ENTER
          : PlayerEvent.FULLSCREEN_EXIT,
        state.fullscreen
      );
    }
  }

  function readFullscreenActive() {
    return !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      (video && video.webkitDisplayingFullscreen)
    );
  }

  function onDocumentFullscreenChange() {
    syncFullscreenState(readFullscreenActive());
  }

  function onWebkitBeginFullscreen() {
    syncFullscreenState(true);
  }

  function onWebkitEndFullscreen() {
    syncFullscreenState(false);
  }

  function bindFullscreenListeners() {
    document.addEventListener("fullscreenchange", onDocumentFullscreenChange);
    document.addEventListener(
      "webkitfullscreenchange",
      onDocumentFullscreenChange
    );
    if (video) {
      video.addEventListener("webkitbeginfullscreen", onWebkitBeginFullscreen);
      video.addEventListener("webkitendfullscreen", onWebkitEndFullscreen);
    }
  }

  function unbindFullscreenListeners() {
    document.removeEventListener(
      "fullscreenchange",
      onDocumentFullscreenChange
    );
    document.removeEventListener(
      "webkitfullscreenchange",
      onDocumentFullscreenChange
    );
  }

  function onLoadStart() {
    bufferMonitor.markLoadStart();
    setPlaybackState(PlaybackState.LOADING);
  }

  function onLoadedMetadata() {
    state.duration = video.duration || 0;
    emitter.emit("durationchange", state.duration);
  }

  function onCanPlay() {
    bufferMonitor.markCanPlay();
    bufferMonitor.markBufferEnd();
    emitter.emit("canplay", snapshot());
    if (
      state.playbackState === PlaybackState.LOADING ||
      state.playbackState === PlaybackState.BUFFERING ||
      state.playbackState === PlaybackState.IDLE
    ) {
      setPlaybackState(
        video.paused ? PlaybackState.READY : PlaybackState.PLAYING
      );
    }
    recovery.reset();
    emitter.emit(PlayerEvent.BUFFER_ENDED, bufferMonitor.getSnapshot());
  }

  function onPlaying() {
    bufferMonitor.markBufferEnd();
    setPlaybackState(PlaybackState.PLAYING);
    recovery.reset();
    emitter.emit(PlayerEvent.PLAYBACK_STARTED, snapshot());
    emitter.emit(PlayerEvent.BUFFER_ENDED, bufferMonitor.getSnapshot());
  }

  function onPause() {
    if (state.playbackState !== PlaybackState.ENDED) {
      setPlaybackState(PlaybackState.PAUSED);
      emitter.emit(PlayerEvent.PLAYBACK_PAUSED, snapshot());
    }
  }

  function onWaiting() {
    if (state.playbackState !== PlaybackState.SEEKING) {
      bufferMonitor.markBufferStart();
      bufferMonitor.markUnderrun();
      setPlaybackState(PlaybackState.BUFFERING);
      emitter.emit(PlayerEvent.BUFFER_STARTED, bufferMonitor.getSnapshot());
    }
  }

  function onSeeking() {
    setPlaybackState(PlaybackState.SEEKING);
    emitter.emit(PlayerEvent.SEEK_STARTED, {
      currentTime: video.currentTime,
    });
  }

  function onSeeked() {
    setPlaybackState(video.paused ? PlaybackState.PAUSED : PlaybackState.PLAYING);
    emitter.emit(PlayerEvent.SEEK_COMPLETED, {
      currentTime: video.currentTime,
    });
  }

  function onTimeUpdate() {
    state.currentTime = video.currentTime || 0;
    var payload = {
      currentTime: state.currentTime,
      duration: state.duration,
    };
    emitter.emit("timeupdate", payload);
    emitter.emit(PlayerEvent.TIME_UPDATE, payload);
  }

  function onProgress() {
    try {
      if (video.buffered && video.buffered.length) {
        state.bufferedEnd = video.buffered.end(video.buffered.length - 1);
        emitter.emit("buffer", { bufferedEnd: state.bufferedEnd });
      }
    } catch (e) {
      /* ignore */
    }
  }

  function onEnded() {
    tpgTrace("STATE", {
      message: "engine_video_ended",
      provider: state.providerName,
      currentTime: state.currentTime,
      duration: state.duration,
    });
    setPlaybackState(PlaybackState.ENDED);
    emitter.emit("ended");
    emitter.emit(PlayerEvent.PLAYBACK_ENDED, snapshot());
  }

  function onError() {
    // Prefer streaming provider errors when present; media error as fallback
    if (activeProvider && state.providerName === "hls.js") {
      return;
    }
    var mediaError = video && video.error;
    var err = {
      code: mediaError ? mediaError.code : 0,
      message: mediaError ? mediaError.message || "Media error" : "Unknown error",
      recoverable: true,
    };
    state.error = err;
    setPlaybackState(PlaybackState.ERROR);
    emitter.emit("error", err);
    emitter.emit(PlayerEvent.PLAYBACK_ERROR, err);

    emitter.emit(PlayerEvent.RETRY_ATTEMPT, { phase: "engine" });
    recovery
      .scheduleRetry(function () {
        if (!state.source) {
          return Promise.resolve(false);
        }
        return load(state.source, { retry: true }).then(function () {
          return play().then(
            function () {
              return true;
            },
            function () {
              return true;
            }
          );
        });
      })
      .then(function (recovered) {
        if (recovered) {
          bufferMonitor.markRecovery({ type: "engine-retry" });
          emitter.emit("recovered", snapshot());
          emitter.emit(PlayerEvent.RECOVERED, snapshot());
        }
      });
  }

  function onRateChange() {
    state.playbackRate = video.playbackRate;
    emitter.emit("ratechange", state.playbackRate);
    emitter.emit(PlayerEvent.RATE_CHANGED, state.playbackRate);
  }

  function onVolumeChange() {
    state.volume = video.volume;
    state.muted = video.muted;
    var payload = { volume: state.volume, muted: state.muted };
    emitter.emit("volumechange", payload);
    emitter.emit(PlayerEvent.VOLUME_CHANGED, payload);
  }

  function onEnterPiP() {
    state.pip = true;
    emitter.emit("pipchange", true);
    emitter.emit(PlayerEvent.PIP_CHANGED, true);
  }

  function onLeavePiP() {
    state.pip = false;
    emitter.emit("pipchange", false);
    emitter.emit(PlayerEvent.PIP_CHANGED, false);
  }

  /**
   * @param {object|string} source
   * @param {object} [opts]
   * @returns {Promise<void>}
   */
  function load(source, opts) {
    opts = opts || {};
    var normalized =
      typeof source === "string"
        ? { src: source }
        : Object.assign({}, source);

    if (!normalized.src) {
      var invalid = createPlaybackError(
        PlaybackErrorCode.InvalidSource,
        "PlaybackEngine: missing source.src",
        { recoverable: false }
      );
      handleProviderError(invalid);
      return Promise.reject(invalid);
    }

    lifecycle.transition(LifecyclePhase.INITIALIZE, { source: normalized.src });
    state.source = normalized;
    state.error = null;
    state.mediaType = normalized.type || detectMediaType(normalized.src);
    normalized.type = state.mediaType;

    lifecycle.transition(LifecyclePhase.RESOLVE_PROVIDER, {
      mediaType: state.mediaType,
    });
    var provider = registry.select(normalized);
    if (!provider) {
      tpgTrace("ERROR", {
        message: "no_provider",
        src: normalized.src,
        type: state.mediaType,
      });
      var noProvider = createPlaybackError(
        PlaybackErrorCode.UnsupportedProvider,
        "No streaming provider supports this source",
        { recoverable: false }
      );
      handleProviderError(noProvider);
      return Promise.reject(noProvider);
    }

    tpgTrace("PROVIDER_SELECTED", {
      provider: provider.getName(),
      src: normalized.src,
      type: state.mediaType,
    });

    lifecycle.transition(LifecyclePhase.CREATE_PROVIDER, {
      provider: provider.getName(),
    });

    var kind =
      typeof provider.getSurfaceKind === "function"
        ? provider.getSurfaceKind()
        : SurfaceKind.VIDEO;
    if (kind === SurfaceKind.EMBED && !embedRoot) {
      tpgTrace("ERROR", {
        message: "embed_surface_missing",
        provider: provider.getName(),
      });
      return Promise.reject(
        createPlaybackError(
          PlaybackErrorCode.ProviderUnavailable,
          "PlaybackEngine: embed surface not attached",
          { recoverable: false, provider: provider.getName() }
        )
      );
    }
    if (kind !== SurfaceKind.EMBED && !video) {
      tpgTrace("ERROR", {
        message: "video_surface_missing",
        provider: provider.getName(),
      });
      return Promise.reject(
        createPlaybackError(
          PlaybackErrorCode.ProviderUnavailable,
          "PlaybackEngine: video element not attached",
          { recoverable: false, provider: provider.getName() }
        )
      );
    }

    setPlaybackState(PlaybackState.LOADING);
    bufferMonitor.markLoadStart();

    bindProvider(provider);
    emitter.emit("sourcechange", normalized);
    emitter.emit(PlayerEvent.SOURCE_CHANGED, {
      source: normalized,
      provider: provider.getName(),
    });

    var loadOpts = Object.assign({}, opts, {
      muted: state.muted,
      volume: state.volume,
    });

    lifecycle.transition(LifecyclePhase.INITIALIZE_PROVIDER, {
      provider: provider.getName(),
    });

    tpgTrace("PROVIDER_INITIALIZED", {
      provider: provider.getName(),
      surfaceKind: kind,
      muted: loadOpts.muted,
    });

    return provider
      .initialize()
      .then(function () {
        return provider.load(normalized, loadOpts);
      })
      .then(function () {
        if (typeof provider.getLevels === "function") {
          quality.setAvailableLevels(provider.getLevels());
        }
        if (kind === SurfaceKind.EMBED) {
          setPlaybackState(PlaybackState.READY);
        }
        // Re-push mute/volume after provider load/ready so early unlock
        // gestures are not lost (critical for Vimeo muted=1 embeds).
        if (activeProvider && typeof activeProvider.setMuted === "function") {
          activeProvider.setMuted(!!state.muted);
        } else if (video) {
          video.muted = !!state.muted;
        }
        if (activeProvider && typeof activeProvider.setVolume === "function") {
          activeProvider.setVolume(state.volume);
        } else if (video) {
          video.volume = state.volume;
        }
        emitter.emit(PlayerEvent.PLAYER_READY, snapshot());
      })
      .catch(function (err) {
        tpgTrace("ERROR", {
          message: "provider_load_failed",
          provider: provider.getName(),
          err: err,
        });
        if (
          state.mediaType === MediaType.HLS &&
          provider.getName() === "hls.js"
        ) {
          handleProviderError(
            err || {
              code: "HLS_LOAD_FAILED",
              message: "Adaptive stream failed to load",
              recoverable: true,
            }
          );
        } else {
          handleProviderError(
            err || {
              code: "LOAD_FAILED",
              message: "Load failed",
              recoverable: true,
            }
          );
        }
        return Promise.reject(err);
      });
  }

  function play() {
    // Keep element mute flag in sync before play() — required for muted
    // autoplay after hard refresh (policy checks the media element attribute).
    if (video) {
      try {
        video.muted = !!state.muted;
        video.defaultMuted = !!state.muted;
      } catch (e) {
        /* ignore */
      }
    }
    if (activeProvider && typeof activeProvider.play === "function") {
      return Promise.resolve(activeProvider.play()).then(function () {
        setPlaybackState(PlaybackState.PLAYING);
        emitter.emit(PlayerEvent.PLAYBACK_STARTED, snapshot());
      });
    }
    if (!video) {
      return Promise.reject(new Error("no video"));
    }
    var p = video.play();
    return p && typeof p.then === "function" ? p : Promise.resolve();
  }

  function pause() {
    if (activeProvider && typeof activeProvider.pause === "function") {
      activeProvider.pause();
      if (state.playbackState !== PlaybackState.ENDED) {
        setPlaybackState(PlaybackState.PAUSED);
        emitter.emit(PlayerEvent.PLAYBACK_PAUSED, snapshot());
      }
      return;
    }
    if (video) {
      video.pause();
    }
  }

  function seek(time) {
    if (!isFinite(time)) return;
    if (activeProvider && typeof activeProvider.seek === "function") {
      activeProvider.seek(Math.max(0, time));
      state.currentTime = time;
      return;
    }
    if (!video) return;
    video.currentTime = Math.max(0, Math.min(time, state.duration || time));
  }

  function setVolume(v) {
    var vol = Math.max(0, Math.min(1, v));
    state.volume = vol;
    if (activeProvider && typeof activeProvider.setVolume === "function") {
      activeProvider.setVolume(vol);
      emitter.emit(PlayerEvent.VOLUME_CHANGED, {
        volume: state.volume,
        muted: state.muted,
      });
      return;
    }
    if (!video) return;
    video.volume = vol;
  }

  function setMuted(m) {
    var prev = state.muted;
    state.muted = !!m;
    if (activeProvider && typeof activeProvider.setMuted === "function") {
      activeProvider.setMuted(state.muted);
    }
    // Always sync the HTMLVideoElement when present — progressive/HLS have no
    // provider setMuted, and embed paths may still leave a sticky muted attr
    // on the hidden video from a prior surface.
    if (video) {
      try {
        video.muted = state.muted;
        video.defaultMuted = state.muted;
        if (!state.muted) {
          video.removeAttribute("muted");
        } else {
          video.setAttribute("muted", "");
        }
      } catch (e) {
        /* ignore */
      }
    }
    tpgTrace("PLAYBACK_STATE_MUTED", {
      muted: state.muted,
      prev: prev,
      provider: state.providerName,
      surfaceKind: state.surfaceKind,
      videoElementMuted: video ? !!video.muted : null,
      muteTarget:
        activeProvider && typeof activeProvider.setMuted === "function"
          ? "provider+video"
          : "HTMLVideoElement",
    });
    tpgTrace("HOST_AUDIO_STATE", {
      engineMuted: state.muted,
      engineVolume: state.volume,
      provider: state.providerName,
      videoMuted: video ? !!video.muted : null,
      videoVolume: video ? video.volume : null,
    });
    var payload = {
      volume: state.volume,
      muted: state.muted,
      _muteChanged: prev !== state.muted,
    };
    emitter.emit(PlayerEvent.VOLUME_CHANGED, payload);
    // Native video volumechange may not fire when only defaultMuted flips —
    // emit the engine alias used by Host persist / UI refresh.
    emitter.emit("volumechange", payload);
    if (prev !== state.muted) {
      emitter.emit(PlayerEvent.MUTE_CHANGED, payload);
      emitter.emit(PlaybackPlatformEvent.MUTE_CHANGED, payload);
    }
  }

  function stop() {
    pause();
    seek(0);
    emitter.emit(PlayerEvent.STOP, snapshot());
    emitter.emit(PlaybackPlatformEvent.STOP, snapshot());
  }

  function setPlaybackRate(rate) {
    if (!video) return;
    video.playbackRate = rate;
  }

  function setQualityLevel(index) {
    quality.selectLevel(index, function (lvl) {
      if (activeProvider && typeof activeProvider.setLevel === "function") {
        activeProvider.setLevel(lvl);
      }
    });
    emitter.emit(PlayerEvent.QUALITY_CHANGED, quality.getSnapshot());
  }

  function toggleFullscreen(container) {
    var el = container || (video && video.parentElement) || video;
    if (!el) {
      return Promise.resolve();
    }

    if (readFullscreenActive()) {
      if (document.exitFullscreen) {
        return document.exitFullscreen().then(function () {
          syncFullscreenState(false);
        });
      }
      if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
        syncFullscreenState(false);
        return Promise.resolve();
      }
      if (video && typeof video.webkitExitFullscreen === "function") {
        try {
          video.webkitExitFullscreen();
        } catch (e) {
          /* ignore */
        }
        syncFullscreenState(false);
        return Promise.resolve();
      }
      return Promise.resolve();
    }

    // iOS Safari: fullscreen a shell div is unsupported — use video element API
    var ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
    var isIOS = /iPad|iPhone|iPod/.test(ua) ||
      (ua.indexOf("Mac") !== -1 &&
        typeof document !== "undefined" &&
        "ontouchend" in document);
    if (
      isIOS &&
      video &&
      typeof video.webkitEnterFullscreen === "function"
    ) {
      try {
        video.webkitEnterFullscreen();
        syncFullscreenState(true);
        return Promise.resolve();
      } catch (e) {
        /* fall through to prefixed element fullscreen */
      }
    }

    if (el.requestFullscreen) {
      return el.requestFullscreen().then(function () {
        syncFullscreenState(true);
      });
    }
    if (el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
      syncFullscreenState(true);
      return Promise.resolve();
    }
    return Promise.resolve();
  }

  var docPipWindow = null;
  var docPipPlaceholder = null;
  var docPipMovedNode = null;

  function canUseElementPiP() {
    if (
      typeof document === "undefined" ||
      !document.pictureInPictureEnabled ||
      !video
    ) {
      return false;
    }
    if (state.surfaceKind === SurfaceKind.EMBED) {
      return false;
    }
    // Cleared / hidden video (post-embed bind) cannot enter element PiP
    if (video.style && video.style.display === "none") {
      return false;
    }
    if (video.disablePictureInPicture) {
      return false;
    }
    // Progressive / HLS may use src, currentSrc, or srcObject (MSE)
    if (!video.src && !video.currentSrc && !video.srcObject) {
      return false;
    }
    return true;
  }

  function describePiPBlocker() {
    if (typeof document === "undefined") {
      return "no_document";
    }
    if (!document.pictureInPictureEnabled) {
      return "pictureInPictureEnabled_false";
    }
    if (!video) {
      return "no_video_element";
    }
    if (state.surfaceKind === SurfaceKind.EMBED) {
      return "surfaceKind_embed";
    }
    if (video.style && video.style.display === "none") {
      return "video_display_none";
    }
    if (video.disablePictureInPicture) {
      return "disablePictureInPicture";
    }
    if (!video.src && !video.currentSrc && !video.srcObject) {
      return "no_media_src";
    }
    return null;
  }

  function enterElementPiP() {
    // Ended / drained media often rejects Element PiP. Seek back synchronously
    // inside the user-gesture stack (do not await play() — that loses activation).
    if (video && video.ended) {
      try {
        video.currentTime = 0;
      } catch (eSeek) {
        /* ignore */
      }
      try {
        var p = video.play();
        if (p && typeof p.catch === "function") {
          p.catch(function () {});
        }
      } catch (ePlay) {
        /* ignore */
      }
    }
    if (
      video &&
      typeof video.webkitSetPresentationMode === "function" &&
      typeof video.webkitSupportsPresentationMode === "function" &&
      video.webkitSupportsPresentationMode("picture-in-picture")
    ) {
      video.webkitSetPresentationMode("picture-in-picture");
      state.pip = true;
      emitter.emit("pipchange", true);
      emitter.emit(PlayerEvent.PIP_CHANGED, true);
      return Promise.resolve({ mode: "webkit" });
    }
    return video.requestPictureInPicture().then(function () {
      state.pip = true;
      emitter.emit("pipchange", true);
      emitter.emit(PlayerEvent.PIP_CHANGED, true);
      return { mode: "element" };
    });
  }

  function restoreDocumentPiP() {
    if (
      docPipMovedNode &&
      docPipPlaceholder &&
      docPipPlaceholder.parentNode
    ) {
      try {
        docPipPlaceholder.parentNode.insertBefore(
          docPipMovedNode,
          docPipPlaceholder
        );
        docPipPlaceholder.parentNode.removeChild(docPipPlaceholder);
      } catch (e) {
        /* ignore */
      }
    }
    docPipMovedNode = null;
    docPipPlaceholder = null;
    if (docPipWindow) {
      try {
        if (!docPipWindow.closed) {
          docPipWindow.close();
        }
      } catch (e2) {
        /* ignore */
      }
    }
    docPipWindow = null;
    if (state.pip) {
      state.pip = false;
      emitter.emit("pipchange", false);
      emitter.emit(PlayerEvent.PIP_CHANGED, false);
    }
  }

  /**
   * Document Picture-in-Picture — used for embed surfaces (YouTube/Vimeo) where
   * the HTMLVideoElement is not the active presentation.
   * @param {HTMLElement} shell
   * @returns {Promise<void>}
   */
  function enterDocumentPiP(shell) {
    if (
      typeof window === "undefined" ||
      !window.documentPictureInPicture ||
      typeof window.documentPictureInPicture.requestWindow !== "function"
    ) {
      return Promise.reject(new Error("Document PiP not supported"));
    }
    if (!shell) {
      return Promise.reject(new Error("PiP shell not available"));
    }
    // Move the full player shell so controls remain available in the PiP window
    var stage = shell;
    return window.documentPictureInPicture
      .requestWindow({
        width: Math.max(320, Math.round((stage.clientWidth || 640) * 0.55)),
        height: Math.max(220, Math.round((stage.clientHeight || 400) * 0.55)),
      })
      .then(function (pipWin) {
        docPipWindow = pipWin;
        docPipPlaceholder = document.createComment("ngp-doc-pip");
        if (stage.parentNode) {
          stage.parentNode.insertBefore(docPipPlaceholder, stage);
        }
        docPipMovedNode = stage;
        // Copy styles so the pip window matches the player chrome
        try {
          var sheets = document.querySelectorAll(
            'link[rel="stylesheet"], style'
          );
          for (var i = 0; i < sheets.length; i++) {
            pipWin.document.head.appendChild(sheets[i].cloneNode(true));
          }
        } catch (e) {
          /* ignore */
        }
        pipWin.document.body.style.margin = "0";
        pipWin.document.body.style.background = "#000";
        pipWin.document.body.appendChild(stage);
        pipWin.addEventListener("pagehide", function () {
          restoreDocumentPiP();
        });
        state.pip = true;
        emitter.emit("pipchange", true);
        emitter.emit(PlayerEvent.PIP_CHANGED, true);
      });
  }

  /**
   * Toggle Picture-in-Picture for the active surface.
   * Element PiP is supported for progressive / HLS (HTMLVideoElement).
   * Embedded YouTube/Vimeo iframes must NOT be moved via Document PiP —
   * relocating cross-origin iframes freezes or crashes playback.
   * @param {HTMLElement} [shell] unused for element PiP; reserved for future
   * @returns {Promise<void>}
   */
  function togglePictureInPicture(shell) {
    var audit = tpgSurfaceAudit({
      providerName: state.providerName,
      surfaceKind: state.surfaceKind,
      video: video,
      embed: embedRoot,
      pipTarget:
        state.surfaceKind === SurfaceKind.EMBED
          ? "none-embed"
          : video
            ? "HTMLVideoElement"
            : "none",
      pipMode:
        state.surfaceKind === SurfaceKind.EMBED ? "unsupported" : "element",
      fullscreenTarget: shell ? "shell" : "unknown",
      muteTarget:
        state.surfaceKind === SurfaceKind.EMBED
          ? "provider:" + state.providerName
          : "HTMLVideoElement",
      volumeTarget:
        state.surfaceKind === SurfaceKind.EMBED
          ? "provider:" + state.providerName
          : "HTMLVideoElement",
      owningComponent: "PlaybackEngine.togglePictureInPicture",
    });
    tpgTrace("PIP_REQUEST", audit);
    tpgTrace("ACTIVE_PROVIDER", { provider: state.providerName });
    tpgTrace("ACTIVE_SURFACE", {
      surfaceKind: state.surfaceKind,
      hasVideo: !!video,
      videoConnected: !!(video && video.isConnected),
    });
    tpgTrace("PIP_TARGET", {
      target: audit.pipTarget,
      videoCurrentSrc: audit.videoCurrentSrc,
      videoSrcObject: audit.videoSrcObject,
    });

    // Exit document PiP if a prior session left one open
    if (docPipWindow && !docPipWindow.closed) {
      tpgTrace("PIP_MODE", { mode: "document-exit" });
      restoreDocumentPiP();
      tpgTrace("PIP_EXIT", { mode: "document" });
      return Promise.resolve();
    }
    if (
      typeof document !== "undefined" &&
      document.pictureInPictureElement
    ) {
      tpgTrace("PIP_MODE", { mode: "element-exit" });
      return document.exitPictureInPicture().then(function () {
        state.pip = false;
        emitter.emit("pipchange", false);
        emitter.emit(PlayerEvent.PIP_CHANGED, false);
        tpgTrace("PIP_EXIT", { mode: "element" });
      });
    }
    if (
      video &&
      typeof video.webkitPresentationMode === "string" &&
      video.webkitPresentationMode === "picture-in-picture" &&
      typeof video.webkitSetPresentationMode === "function"
    ) {
      tpgTrace("PIP_MODE", { mode: "webkit-exit" });
      video.webkitSetPresentationMode("inline");
      state.pip = false;
      emitter.emit("pipchange", false);
      emitter.emit(PlayerEvent.PIP_CHANGED, false);
      tpgTrace("PIP_EXIT", { mode: "webkit" });
      return Promise.resolve();
    }

    var supported = canUseElementPiP();
    var blocker = describePiPBlocker();
    tpgTrace("PIP_SUPPORTED", {
      supported: supported,
      blocker: blocker,
      capabilitiesSupportsPiP: !!(
        state.capabilities && state.capabilities.supportsPiP
      ),
    });
    tpgTrace("PIP_MODE", {
      mode: supported ? "element-enter" : "rejected",
      blocker: blocker,
    });

    if (supported) {
      return enterElementPiP()
        .then(function (info) {
          tpgTrace("PIP_ENTER_SUCCESS", {
            mode: (info && info.mode) || "element",
            provider: state.providerName,
            videoCurrentSrc: video ? String(video.currentSrc || "").slice(0, 80) : null,
          });
        })
        .catch(function (err) {
          tpgTrace("PIP_ENTER_FAILURE", {
            code: err && err.code,
            name: err && err.name,
            message: err && err.message,
            provider: state.providerName,
            blocker: blocker,
          });
          return Promise.reject(err);
        });
    }

    // Embed / unsupported — graceful reject (UI announces; do not move iframe)
    var rejectErr = Object.assign(
      new Error("PiP is not available for this media"),
      {
        code: "PIP_UNSUPPORTED",
        surfaceKind: state.surfaceKind,
        provider: state.providerName,
        blocker: blocker,
      }
    );
    tpgTrace("PIP_ENTER_FAILURE", {
      code: rejectErr.code,
      blocker: blocker,
      provider: state.providerName,
      surfaceKind: state.surfaceKind,
    });
    return Promise.reject(rejectErr);
  }

  function destroy() {
    recovery.cancel();
    pause();
    restoreDocumentPiP();
    if (
      typeof document !== "undefined" &&
      video &&
      document.pictureInPictureElement === video
    ) {
      try {
        document.exitPictureInPicture();
      } catch (e) {
        /* ignore */
      }
    }
    clearProvider();
    quality.destroy();
    bufferMonitor.destroy();
    if (video) {
      video.removeAttribute("src");
      try {
        video.load();
      } catch (e) {
        /* ignore */
      }
    }
    unbindVideo();
    lifecycle.markDestroyed();
    state.lifecyclePhase = lifecycle.getPhase();
    try {
      emitter.emit(PlaybackPlatformEvent.DESTROYED, snapshot());
    } catch (e) {
      /* ignore */
    }
    emitter.removeAllListeners();
    state.playbackState = PlaybackState.IDLE;
  }

  // Forward buffer bandwidth into quality manager
  bufferMonitor.subscribe(function (type, payload) {
    if (type === "sample" && payload && payload.estimatedBandwidth) {
      quality.setEstimatedBandwidth(payload.estimatedBandwidth);
      emitter.emit(PlayerEvent.NETWORK_UPDATE, payload);
    }
  });

  return {
    attach: bindVideo,
    attachMediaSurface: attachMediaSurface,
    detach: unbindVideo,
    getVideoElement: function () {
      return video;
    },
    getEmbedElement: function () {
      return embedRoot;
    },
    load: load,
    play: play,
    pause: pause,
    stop: stop,
    seek: seek,
    setVolume: setVolume,
    setMuted: setMuted,
    setPlaybackRate: setPlaybackRate,
    setQualityLevel: setQualityLevel,
    getQuality: function () {
      return quality.getSnapshot();
    },
    getBufferStats: function () {
      return bufferMonitor.getSnapshot();
    },
    getProviderName: function () {
      return state.providerName;
    },
    getSurfaceKind: function () {
      return state.surfaceKind;
    },
    getCapabilities: function () {
      return capabilities.getCapabilities();
    },
    hasCapability: function (key) {
      return capabilities.hasCapability(key);
    },
    getLifecyclePhase: function () {
      return lifecycle.getPhase();
    },
    getLifecycleHistory: function () {
      return lifecycle.getHistory();
    },
    getEventBus: function () {
      return emitter;
    },
    listStreamingProviders: function () {
      return registry.list();
    },
    toggleFullscreen: toggleFullscreen,
    togglePictureInPicture: togglePictureInPicture,
    getProviderAudioTracks: function () {
      if (activeProvider && typeof activeProvider.getAudioTracks === "function") {
        return activeProvider.getAudioTracks();
      }
      return [];
    },
    setProviderAudioTrack: function (index) {
      if (activeProvider && typeof activeProvider.setAudioTrack === "function") {
        activeProvider.setAudioTrack(index);
      }
    },
    getProviderSubtitleTracks: function () {
      if (activeProvider && typeof activeProvider.getSubtitleTracks === "function") {
        return activeProvider.getSubtitleTracks();
      }
      return [];
    },
    setProviderSubtitles: function (id) {
      if (!activeProvider) {
        return;
      }
      var off = id == null || id === "off" || id === false || id === "";
      if (typeof activeProvider.setSubtitleDisplay === "function") {
        activeProvider.setSubtitleDisplay(!off);
      }
      if (typeof activeProvider.setSubtitleTrack !== "function") {
        return;
      }
      if (off) {
        activeProvider.setSubtitleTrack(-1);
        return;
      }
      var idx = 0;
      if (typeof id === "string") {
        if (id.indexOf("native-") === 0) {
          idx = parseInt(id.slice(7), 10);
        } else if (id.indexOf("hls-sub-") === 0) {
          idx = parseInt(id.slice(8), 10);
        }
      } else if (typeof id === "number") {
        idx = id;
      }
      if (isNaN(idx) || idx < 0) {
        idx = 0;
      }
      var tracks =
        typeof activeProvider.getSubtitleTracks === "function"
          ? activeProvider.getSubtitleTracks()
          : [];
      if (tracks && tracks.length) {
        if (idx >= tracks.length) {
          idx = 0;
        }
        activeProvider.setSubtitleTrack(idx);
      }
    },
    getState: snapshot,
    getPlaybackState: function () {
      return state.playbackState;
    },
    on: emitter.on,
    off: emitter.off,
    once: emitter.once,
    publish: emitter.publish,
    subscribe: emitter.subscribe,
    destroy: destroy,
  };
}

export default createPlaybackEngine;
